/**
 * The user secret
 * ===============
 *
 * Every PubHubs account has one user secret: 32 random bytes, generated by the global client the
 * first time the user registers. It is the key under which everything the account stores at the
 * PubHubs Central (PHC) object store is encrypted, so losing it means losing the account.
 *
 * PHC never learns the secret itself. The client stores it at PHC encrypted, once for every
 * identifying attribute (email address, phone number, PubHubs card, ...) the user can log in with.
 * The key used for an attribute is the attribute key, which the authentication server hands out
 * (`attrKeysEP`) to whoever proves ownership of that attribute in Yivi. Disclosing a single
 * identifying attribute is therefore enough to recover the user secret, while PHC's own data is not.
 *
 * Layout of the stored object
 * ---------------------------
 *
 *     { version: 1, data: { <attrId>: { <attrValue>: { ts, encUserSecret } } } }
 *
 * Every identifying attribute the account can be entered with needs an entry of its own, and an
 * attribute added later needs one too: a PubHubs card is disclosed on its own at the next login, so
 * an account whose card has no entry cannot recover its secret from anything and is lost. The entry
 * is normally written by the login that disclosed the attribute; `addIdentifyingAttr()` is for the
 * card that arrives after that login, when there is nothing disclosed left to decrypt the secret
 * with and the session's own copy is what it is stored from.
 *
 * `encUserSecret` is the user secret encrypted with the attribute key, and `ts` records which
 * generation of that key was used. The `ts` is sent along to the authentication server so it knows
 * which `old_key` to return next to the `latest_key`. Objects written before `version` existed hold
 * the bare inner map (`UserSecretData`); `isUserSecretObjectNew()`/`getUserSecretData()` accept both
 * shapes and the next login rewrites such an object in the current format.
 *
 * Two copies: `usersecret` and `usersecretbackup`
 * ----------------------------------------------
 * Attribute keys are rotated, so every login rewrites the object with the secret re-encrypted under
 * the latest keys. An interrupted rewrite - or one that turns out not to decrypt - would leave the
 * account unrecoverable, so the object is kept twice and the two copies are never written at the
 * same time. `storeUserSecretObject()` writes them strictly in this order:
 *
 *   1. `usersecret` is (over)written with the freshly encrypted object;
 *   2. that object is read back and decrypted again, and the result is compared with the secret that
 *      should have been stored. If this fails, storing stops here and `usersecretbackup` still holds
 *      the previous, known-good object;
 *   3. only then is `usersecretbackup` overwritten with the very same bytes;
 *   4. only then is the secret itself cached, in memory and in local storage.
 *
 * `getUserSecretObject()` reads the two copies back and reports whether they agree, and
 * `resolveUserSecretObject()` repairs them where it can. Together they undo the order above:
 *
 *   - no `usersecret` at all: a fresh account, a new secret is generated when storing;
 *   - both copies equal: nothing was interrupted, they are used as they are;
 *   - the copies differ: `usersecret` is a rewrite from step 1 that never reached step 3, so
 *     `usersecretbackup` is the authoritative one and is copied back over `usersecret`. Both are
 *     decrypted first to check that they really do hold the same secret; if they hold *different*
 *     secrets, something is wrong that the client cannot repair on its own, and the user is shown an
 *     error instead of being silently switched to one of the two;
 *   - a `usersecret` in the current format but no `usersecretbackup`: an earlier login was
 *     interrupted between steps 2 and 3, or the backup was removed. `usersecret` was verified in
 *     step 2 before the backup write was attempted, so it is the best copy there is: it is used, and
 *     the store at the end of the login recreates the backup from it. Refusing to log in here would
 *     lock the user out for good, because every later attempt would find the same state.
 *
 * Where the secret lives after login
 * ----------------------------------
 * Once stored, the secret is kept base64 encoded in this class and in local storage under
 * `UserSecret`, and handed out by `getUserSecretInfo()` to encrypt and decrypt the account's other
 * objects. It is dropped from local storage on logout; if it is gone while the user is still logged
 * in, they must have edited their own local storage and are logged out.
 */
// Packages
import { Buffer } from 'node:buffer';

// Logic
import { base64fromBase64Url, buffersAreEqual } from '@global-client/logic/utils/mssUtils';

import { createLogger } from '@hub-client/logic/logging/Logger';
import { assert } from '@hub-client/logic/utils/assert';

// Models
import { type AttrKeyResp } from '@global-client/models/MSS/TAuths';
import { type SignedIdentifyingAttrs } from '@global-client/models/MSS/TGeneral';
import * as TPHC from '@global-client/models/MSS/TPHC';

// Stores
import { useGlobal } from '@global-client/stores/global';

const logger = createLogger('UserSecret');

/**
 * The part of the PHC server this class needs: the object store it keeps the user secret objects in,
 * the encryption used for them, and the logout it falls back on. Declared structurally so that the
 * dependency stays one-way - PHCServer knows about the user secret, not the other way around.
 */
type UserSecretObjectStore = {
	getUserObject(handle: string): Promise<{ object: ArrayBuffer | null; details: TPHC.UserObjectDetails } | null>;
	storeObject(handle: string, data: Uint8Array, overwriteHash?: string): Promise<boolean>;
	encryptData(data: Uint8Array, key: Uint8Array): Promise<Uint8Array>;
	decryptData(ciphertext: Uint8Array, key: string): Promise<Uint8Array>;
	triggerLogoutProcedure(): void;
};

/** Requests the attribute keys needed to decrypt (and re-encrypt) the given user secret object. */
type AttrKeysFetcher = (
	userSecretObject: TPHC.UserSecretObject | null,
) => Promise<{ error: false; response: Record<string, AttrKeyResp> } | { error: true; response: string }>;

/** The user secret objects as stored, see {@link UserSecretManager.getUserSecretObject}. */
type UserSecretObjects =
	| {
			objectsEqual: true;
			userSecret: { object: TPHC.UserSecretObject; details: TPHC.UserObjectDetails } | null;
			userSecretBackup: null;
	  }
	| {
			objectsEqual: boolean;
			userSecret: { object: TPHC.UserSecretObjectNew; details: TPHC.UserObjectDetails };
			userSecretBackup: { object: TPHC.UserSecretObjectNew; rawData: ArrayBuffer; details: TPHC.UserObjectDetails };
	  };

/** The user secret objects after any difference between them has been repaired, or the error to show. */
type ResolvedUserSecretObjects =
	| { error: true; message: string; values?: string[] }
	| {
			error: false;
			userSecret: { object: TPHC.UserSecretObject; details: TPHC.UserObjectDetails } | null;
			userSecretBackup: { object: TPHC.UserSecretObjectNew; details: TPHC.UserObjectDetails } | null;
	  };

export default class UserSecretManager {
	private readonly _objectStore: UserSecretObjectStore;
	/** NOTE: Do not use this variable directly, but use getUserSecretInfo(). */
	private _userSecret: string | undefined;

	constructor(objectStore: UserSecretObjectStore) {
		this._objectStore = objectStore;
	}

	// #region Retrieving the user secret

	/**
	 * Drop the cached user secret, so that it is not handed to the next account to log in - see
	 * {@link PHCServer.reset}.
	 */
	forgetUserSecret() {
		this._userSecret = undefined;
	}

	/**
	 * The user secret of the logged in user, base64 encoded, from memory or from local storage.
	 *
	 * @returns The user secret, or undefined if it is gone and the logout procedure was triggered.
	 */
	async getUserSecretInfo() {
		const global = useGlobal();
		assert.isNotNull(global.loggedIn, 'The user secret cannot be requested if a user is not logged in.');
		if (this._userSecret) {
			return this._userSecret;
		}
		const storedUserSecret = localStorage.getItem('UserSecret');
		// This will only happen when a user is messing with their local storage, which means the logout procedure will be invoked.
		if (!storedUserSecret) {
			this._objectStore.triggerLogoutProcedure();
			return;
		}
		this._userSecret = storedUserSecret;
		return this._userSecret;
	}

	/**
	 * Request the user secret objects from the object store, and report whether the two stored copies
	 * are still equal. See the note at the top of this file for what each outcome means.
	 *
	 * @returns An object with (a) objectsEqual - a boolean denoting whether or not the usersecret and usersecretbackup objects are equal;
	 *                         (b) userSecret - an object with the decoded usersecret and its details if the object exists, null otherwise; and
	 *                         (c) userSecretBackup - an object with the decoded backup usersecret and its details if it exists, null otherwise.
	 */
	async getUserSecretObject(): Promise<UserSecretObjects> {
		const decodedObjResp = await this._decodeUserSecretObject<TPHC.UserSecretObject>('usersecret');

		// Nothing stored yet: this is a new account, a user secret will be generated when storing.
		if (!decodedObjResp) {
			return { objectsEqual: true, userSecret: null, userSecretBackup: null };
		}
		const { rawObjectData: rawUserSecretObj, decodedObject: decodedUserSecretObj } = decodedObjResp;
		// Objects still in the format that predates the backup have nothing to be compared against.
		if (!TPHC.isUserSecretObjectNew(decodedUserSecretObj)) {
			return { objectsEqual: true, userSecret: { object: decodedUserSecretObj, details: rawUserSecretObj.details }, userSecretBackup: null };
		}

		const decodedBackupObjResp = await this._decodeUserSecretObject<TPHC.UserSecretObjectNew>('usersecretbackup');
		// The backup is only written after usersecret has been read back and verified, so a missing
		// backup means an earlier login was interrupted in between (or the object was removed).
		// usersecret is then the only copy left, and a verified one, so continue with it and let the
		// store at the end of this login recreate the backup from it. Refusing to continue would lock
		// the user out of their account for good, since every later login would find the same state.
		if (!decodedBackupObjResp) {
			logger.warn('No usersecretbackup object is stored; continuing with the usersecret object, which the next store will back up again.');
			return { objectsEqual: true, userSecret: { object: decodedUserSecretObj, details: rawUserSecretObj.details }, userSecretBackup: null };
		}
		const { rawObjectData: rawBackupUserSecretObj, decodedObject: decodedBackupUserSecretObj } = decodedBackupObjResp;
		return {
			objectsEqual: buffersAreEqual(rawUserSecretObj.object, rawBackupUserSecretObj.object),
			userSecret: { object: decodedUserSecretObj, details: rawUserSecretObj.details },
			userSecretBackup: { object: decodedBackupUserSecretObj, rawData: rawBackupUserSecretObj.object, details: rawBackupUserSecretObj.details },
		};
	}

	/**
	 * Request the user secret objects and, if the two stored copies differ, reset usersecret to
	 * usersecretbackup so that the login can continue from a consistent state.
	 *
	 * @param signedIdentifyingAttrs A list of the signed identifying attributes that were disclosed in the enter request.
	 * @param requestAttrKeys Requests the attribute keys belonging to a user secret object.
	 * @param afterReset Whether this call is the check performed after resetting usersecret to usersecretbackup.
	 * @returns The user secret objects, or the translation key of the error to show the user.
	 */
	async resolveUserSecretObject(
		signedIdentifyingAttrs: SignedIdentifyingAttrs,
		requestAttrKeys: AttrKeysFetcher,
		afterReset: boolean = false,
	): Promise<ResolvedUserSecretObjects> {
		try {
			const { objectsEqual, userSecret, userSecretBackup } = await this.getUserSecretObject();
			if (objectsEqual) {
				return { error: false, userSecret, userSecretBackup };
			}

			// After resetting the usersecret object to the usersecretbackup object, the objects should be equal. Otherwise some error occured.
			if (afterReset) {
				logger.error('Unexpected difference in usersecret and usersecretbackup objects after a reset.');
				return { error: true, message: 'errors.general_error' };
			}

			// If objects are not equal: try to decrypt both objects and compare the (decrypted) user secret that is stored
			const attrKeyRespOriginal = await requestAttrKeys(userSecret.object);
			const attrKeyRespBackup = await requestAttrKeys(userSecretBackup.object);
			if (attrKeyRespOriginal.error) {
				return { error: true, message: attrKeyRespOriginal.response };
			} else if (attrKeyRespBackup.error) {
				return { error: true, message: attrKeyRespBackup.response };
			}
			const resp = await this._decryptAndResetUserSecret(
				signedIdentifyingAttrs,
				userSecret,
				attrKeyRespOriginal.response,
				userSecretBackup,
				attrKeyRespBackup.response,
			);

			// If the user secrets are not equal, notify the user.
			if (!resp.equalSecret) {
				logger.error('Different user secrets are stored in the usersecret and usersecretbackup objects.');
				return { error: true, message: 'errors.general_error' };
			}

			// If resetting the usersecret was not successful, notify the user of an error that occured.
			if (!resp.stored) {
				logger.error('Resetting the user secret was not successful.');
				return { error: true, message: 'errors.general_error' };
			}

			// After a successful reset: check again to see if the objects are now equal.
			return this.resolveUserSecretObject(signedIdentifyingAttrs, requestAttrKeys, true);
		} catch (error) {
			logger.error('An error occured when requesting the user secret object.', error);
			return { error: true, message: 'errors.general_error' };
		}
	}

	/**
	 * Retrieve a user secret object from the object store and parse it.
	 *
	 * @param handle The handle the object is stored under.
	 * @returns The raw object with its details and the parsed object, or null if the object does not exist.
	 */
	private async _decodeUserSecretObject<T>(
		handle: string,
	): Promise<{ rawObjectData: { object: ArrayBuffer; details: TPHC.UserObjectDetails }; decodedObject: T } | null> {
		const objectResp = await this._objectStore.getUserObject(handle);
		if (objectResp === null || objectResp.object === null) {
			return null;
		}
		const { object, details } = objectResp;
		const decoder = new TextDecoder();
		const decodedUserSecret = decoder.decode(objectResp.object);
		return { rawObjectData: { object, details }, decodedObject: JSON.parse(decodedUserSecret) as T };
	}

	/**
	 * Check that the usersecret and usersecretbackup objects hold the same user secret and, if so,
	 * overwrite the usersecret object with the (authoritative) backup.
	 *
	 * @param identifyingAttrs The signed identifying attributes the user revealed.
	 * @param userSecret An object with both the usersecret object and the details of the object.
	 * @param attrKeysUserSecret The attribute keys as requested for the usersecret object.
	 * @param backupUserSecret An object with the backup usersecret object, the raw data (before parsing) of the usersecret object and the details of the object.
	 * @param attrKeysBackupUserSecret The attribute keys as requested for the backup usersecret object.
	 * @returns Whether both objects hold the same user secret and, if they do, whether resetting the usersecret object succeeded.
	 */
	private async _decryptAndResetUserSecret(
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecret: { object: TPHC.UserSecretObjectNew; details: TPHC.UserObjectDetails },
		attrKeysUserSecret: Record<string, AttrKeyResp>,
		backupUserSecret: { object: TPHC.UserSecretObjectNew; rawData: ArrayBuffer; details: TPHC.UserObjectDetails },
		attrKeysBackupUserSecret: Record<string, AttrKeyResp>,
	): Promise<{ equalSecret: false } | { equalSecret: true; stored: boolean }> {
		const userSecretData = TPHC.getUserSecretData(userSecret.object);
		const userSecretDecrypted = await this._decryptUserSecretAllAttr(identifyingAttrs, attrKeysUserSecret, userSecretData);
		const backupData = TPHC.getUserSecretData(backupUserSecret.object);
		const backupDecrypted = await this._decryptUserSecretAllAttr(identifyingAttrs, attrKeysBackupUserSecret, backupData);

		// The usersecrets stored in the usersecret and usersecretbackup objects are expected to be equal.
		if (!buffersAreEqual(userSecretDecrypted, backupDecrypted)) {
			return { equalSecret: false };
		}

		try {
			const stored = await this._objectStore.storeObject('usersecret', new Uint8Array(backupUserSecret.rawData), userSecret.details.hash);
			return { equalSecret: true, stored };
		} catch (e) {
			logger.error('An error occured while trying to store the user secret.', e);
			return { equalSecret: true, stored: false };
		}
	}

	/**
	 * Decrypt the user secret with the attribute key of every identifying attribute it was stored for,
	 * and check that all of them yield the same secret.
	 *
	 * @param identifyingAttrs The signed identifying attributes the user revealed.
	 * @param attrKeyResp The response from the attrKeysEP with the requested attribute keys.
	 * @param userSecretData The encrypted user secret per attribute type and value.
	 * @param useNewKey Whether to decrypt with the latest attribute key instead of the key the secret was stored with.
	 * @returns The user secret.
	 * @throws Will throw an error if a key is missing, the secrets do not match, or the secret cannot be recovered at all.
	 */
	private async _decryptUserSecretAllAttr(
		identifyingAttrs: SignedIdentifyingAttrs,
		attrKeyResp: Record<string, AttrKeyResp>,
		userSecretData: TPHC.UserSecretData,
		useNewKey: boolean = false,
	) {
		let referenceUserSecret: Uint8Array | null = null;
		for (const [handle, attr] of Object.entries(identifyingAttrs)) {
			const key = useNewKey ? attrKeyResp[handle].latest_key[0] : attrKeyResp[handle].old_key;
			// If the user secret was not stored before for this combination of attribute type and value, continue.
			if (!userSecretData[attr.id]?.[attr.value]) {
				continue;
			}
			// If there is no old_key in the attrKeyResp for this attribute, something went wrong
			if (key === null) {
				throw new Error(`Expected an old_key in the attrKeyResp for attribute with type ${attr.id} and value ${attr.value}`);
			}
			// Try to decrypt the user secret
			const decryptedUserSecret = await this._decryptUserSecret(key, userSecretData[attr.id][attr.value]);
			if (referenceUserSecret === null) {
				referenceUserSecret = decryptedUserSecret;
			} else if (!buffersAreEqual(referenceUserSecret, decryptedUserSecret)) {
				throw new Error('The user secrets for different identifying attributes do not match.');
			}
		}

		if (referenceUserSecret === null) {
			throw new Error('Could not recover the user secret, it is not stored for any of the provided identifying attributes.');
		}
		return referenceUserSecret;
	}

	private async _decryptUserSecret(oldAttrKey: string, userSecretObject: { ts: string; encUserSecret: string }) {
		const encUserSecret = new Uint8Array(Buffer.from(userSecretObject.encUserSecret, 'base64'));
		const userSecret = await this._objectStore.decryptData(encUserSecret, oldAttrKey);
		return userSecret;
	}

	// #endregion

	// #region Storing the user secret

	/**
	 * Stores the user secret object at the PHC object store under the handles 'usersecret' and
	 * 'usersecretbackup'.
	 *
	 * The two objects are written one after the other, never at the same time: 'usersecret' is stored
	 * first and read back and decrypted to check that it can still be recovered, and only then is
	 * 'usersecretbackup' overwritten with the same bytes, and only then is the secret itself cached.
	 * That way an interrupted or corrupted write always leaves 'usersecretbackup' holding the previous,
	 * known-good object, which the next login falls back on. See the note at the top of this file.
	 *
	 * @param attrKeyResp The response from the attrKeysEP with the requested attribute keys.
	 * @param identifyingAttrs A list of the signed identifying attributes that were disclosed in the enter request.
	 * @param userSecretObject The data for the existing user secret object.
	 * @param userSecretObjectDetails The object details for the existing user secret object.
	 * @param useSessionSecret Take the user secret from the session instead of decrypting it with the
	 * attributes in `identifyingAttrs`. Only for an attribute added to an account this session already
	 * entered, see {@link addIdentifyingAttr}.
	 * @throws Will throw an error if an old attribute key is missing in the attrKeyResp.
	 * @throws Will throw an error if either object could not be stored, or if the stored usersecret object does not decrypt to the user secret.
	 */
	async storeUserSecretObject(
		attrKeyResp: Record<string, AttrKeyResp>,
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecretObject: TPHC.UserSecretObject | null,
		userSecretObjectDetails: { usersecret: TPHC.UserObjectDetails; backup: TPHC.UserObjectDetails | null } | null,
		useSessionSecret: boolean = false,
	): Promise<void> {
		const computedUserSecretObject = await this._computeNewUserSecretObject(attrKeyResp, identifyingAttrs, userSecretObject, useSessionSecret);
		const encodedNewUserSecretObject: Uint8Array = new TextEncoder().encode(JSON.stringify(computedUserSecretObject.newUserSecretObject));

		// Store the userSecret object
		const overwriteHash = userSecretObjectDetails?.usersecret?.hash;
		const storedObject = await this._objectStore.storeObject('usersecret', encodedNewUserSecretObject, overwriteHash);

		if (!storedObject) {
			throw new Error('The user secret was not properly stored.');
		}
		// Check whether the renewed user secret can be correctly decrypted
		const storedUserSecret = await this._decodeUserSecretObject<TPHC.UserSecretObject>('usersecret');
		assert.isNotNull(storedUserSecret, 'Could not retrieve the user secret object even though it seems to be properly stored.');

		const userSecretData = TPHC.getUserSecretData(storedUserSecret.decodedObject);
		const decryptedUserSecret = await this._decryptUserSecretAllAttr(identifyingAttrs, attrKeyResp, userSecretData, true);
		if (!buffersAreEqual(computedUserSecretObject.userSecret, decryptedUserSecret)) {
			throw new Error('The decrypted stored user secret does not match the user secret that should have been stored.');
		}

		// Now that we are certain the usersecret object was properly stored, update usersecretbackup to the new value of usersecret
		const overwriteHashBackup = userSecretObjectDetails?.backup?.hash;
		const storedBackup = await this._objectStore.storeObject('usersecretbackup', encodedNewUserSecretObject, overwriteHashBackup);

		// Only set the _userSecret variable and store the user secret in localStorage if the backupusersecret object was also successfully written to the object store.
		if (!storedBackup) {
			throw new Error('The user secret backup was not properly stored.');
		}

		// Encode the userSecret as a base64 string
		this._userSecret = Buffer.from(computedUserSecretObject.userSecret).toString('base64');
		localStorage.setItem('UserSecret', this._userSecret);
	}

	/**
	 * Store the user secret for an identifying attribute that was added to the account after the
	 * login that stored it, the PubHubs card of a registration whose issuance had to be retried.
	 *
	 * Without this the account is lost: the card is the only attribute a later login discloses, and
	 * the secret would not be stored for it, so nothing that login has can recover it. The secret is
	 * taken from the session rather than decrypted, because the attribute that could decrypt it is
	 * not disclosed any more by the time the card arrives.
	 *
	 * @param attrKeyResp The response from the attrKeysEP with the key for the added attribute.
	 * @param identifyingAttrs The attribute that was added to the account.
	 * @param userSecretObject The data for the existing user secret object.
	 * @param userSecretObjectDetails The object details for the existing user secret object.
	 * @throws Whatever {@link storeUserSecretObject} throws, plus an error when this session has no
	 * user secret to store.
	 */
	async addIdentifyingAttr(
		attrKeyResp: Record<string, AttrKeyResp>,
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecretObject: TPHC.UserSecretObject | null,
		userSecretObjectDetails: { usersecret: TPHC.UserObjectDetails; backup: TPHC.UserObjectDetails | null } | null,
	): Promise<void> {
		return this.storeUserSecretObject(attrKeyResp, identifyingAttrs, userSecretObject, userSecretObjectDetails, true);
	}

	/**
	 * Generates a new user secret or decrypt the existing user secret.
	 * Then encrypts the user secret with the new attribute key for each identifying attribute that was disclosed in the enter request.
	 *
	 * @param attrKeyResp The response from the attrKeysEP with the requested attribute keys.
	 * @param identifyingAttrs A list of the signed identifying attributes that were disclosed in the enter request.
	 * @param userSecretObject The data for the existing user secret object.
	 * @param useSessionSecret Take the user secret from the session rather than decrypting it, see
	 * {@link storeUserSecretObject}.
	 * @returns The updated user secret object.
	 * @throws Will throw an error if an old attribute key is missing in the attrKeyResp.
	 * @throws Will throw an error if the user secrets encrypted with different attribute keys do not match.
	 * @throws Will throw an error if the user secret could not successfully be decrypted.
	 */
	private async _computeNewUserSecretObject(
		attrKeyResp: Record<string, AttrKeyResp>,
		identifyingAttrs: SignedIdentifyingAttrs,
		userSecretObject: TPHC.UserSecretObject | null,
		useSessionSecret: boolean = false,
	): Promise<{ newUserSecretObject: TPHC.UserSecretObjectNew; userSecret: Uint8Array }> {
		let newUserSecretData: TPHC.UserSecretData = {};
		if (userSecretObject !== null) {
			newUserSecretData = TPHC.getUserSecretData(userSecretObject);
		}
		let userSecret: Uint8Array | null = null;
		if (useSessionSecret) {
			// The attribute being added is the only one disclosed, and the secret was never stored for
			// it, so there is nothing here to decrypt it with - it comes from the login that stored it.
			const sessionSecret = await this.getUserSecretInfo();
			if (!sessionSecret) {
				throw new Error('The user secret of this session is gone, so it cannot be stored for another attribute.');
			}
			userSecret = new Uint8Array(Buffer.from(sessionSecret, 'base64'));
		} else if (userSecretObject === null) {
			// If this is the first time the user secret object is set, a random 256 bits (32 bytes) user secret needs to be generated.
			userSecret = globalThis.crypto.getRandomValues(new Uint8Array(32));
		} else {
			userSecret = await this._decryptUserSecretAllAttr(identifyingAttrs, attrKeyResp, newUserSecretData);
		}

		// Encrypt the user secret with the new attribute key for each of the identifying attributes used in this enter request.
		for (const [handle, attr] of Object.entries(identifyingAttrs)) {
			const keyResp = attrKeyResp[handle];
			// Encrypt the userSecret with the new attribute key and compose the new userSecret object
			const encodedKey = new Uint8Array(Buffer.from(base64fromBase64Url(keyResp.latest_key[0]), 'base64'));
			const cipherText = await this._objectStore.encryptData(userSecret, encodedKey);
			// Use local OR assignment operator (||=) to make sure that newUserSecretObject is initialized before assigning the nested property
			(newUserSecretData[attr.id] ||= {})[attr.value] = { ts: keyResp.latest_key[1], encUserSecret: Buffer.from(cipherText).toString('base64') };
		}
		const newUserSecretObject: TPHC.UserSecretObject = {
			version: 1,
			data: newUserSecretData,
		};
		return { newUserSecretObject, userSecret };
	}

	// #endregion
}

export { type AttrKeysFetcher, type ResolvedUserSecretObjects, type UserSecretObjectStore, type UserSecretObjects };
